"use client";

import { PASTEBOARD_TOKEN_SEARCH_PARAM_KEY } from "@/const/links";
import Image from "next/image";
import { useState } from "react";
import { Check } from "lucide-react";
import DetourIcon from "@/assets/icons/detour-icon.svg";
import { LANDING_PAGE_URL } from "@/const/external-links";
import { getCopyLinkCopy } from "@/const/copyLinkTranslations";
import Link from "next/link";
import { useInAppEscape } from "@/lib/inAppEscape/InAppEscapeContext";

type GetAppWithLinkCopyProps = {
  redirectionUrl: string | undefined;
  pasteboardToken: string | null;
  appIconSrc: string | null;
  appName: string | null;
  customHtml?: string | null;
  darkTheme?: boolean;
  language?: string;
};

const BTN_BASE = `
  inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold
  transition-colors
`;

const BTN_PRIMARY_LIGHT = `${BTN_BASE} border border-gray-900 bg-gray-900 text-white hover:bg-black`;
const BTN_PRIMARY_DARK = `${BTN_BASE} border border-white bg-white text-black hover:bg-neutral-200`;

const BTN_SECONDARY_LIGHT = `${BTN_BASE} border border-slate-300 bg-transparent text-gray-900 hover:bg-slate-50`;
const BTN_SECONDARY_DARK = `${BTN_BASE} border border-zinc-700 bg-transparent text-zinc-50 hover:bg-zinc-900`;

function GetAppWithLinkCopy({
  redirectionUrl,
  pasteboardToken,
  appIconSrc,
  appName,
  customHtml,
  darkTheme = false,
  language,
}: GetAppWithLinkCopyProps) {
  const [isCopied, setIsCopied] = useState(false);
  const copy = getCopyLinkCopy(language);
  // Non-null only in browsers that refuse to open the store link. See
  // lib/inAppEscape.
  const inAppEscape = useInAppEscape();

  const copyUrlToClipboard = async () => {
    const url = new URL(window.location.href);

    if (pasteboardToken) {
      url.searchParams.set(PASTEBOARD_TOKEN_SEARCH_PARAM_KEY, pasteboardToken);
    }
    await navigator.clipboard.writeText(url.toString());
    setIsCopied(true);
  };

  // Both call sites are button taps, so the gesture-only attempts are in play.
  // False whenever the visitor is somewhere this URL opens normally, which is
  // most of the time. Nothing is rendered when every scheme is swallowed: the
  // visitor stays on this screen with both buttons live and can tap again.
  const startEscape = () =>
    Boolean(
      redirectionUrl && inAppEscape?.start(redirectionUrl, { fromTap: true })
    );

  const redirectToStore = () => {
    if (!redirectionUrl || startEscape()) return;

    window.location.replace(redirectionUrl);
  };

  const handleCopyAndInstall = async () => {
    // Caught rather than left bare: writeText rejects on a denied permission or
    // a non-secure context, and an uncaught rejection here would take the
    // install redirect down with it — the copy is the nice-to-have, the store
    // is the point.
    const copying = copyUrlToClipboard().catch(() => {});

    // Not awaited on the escape path. Awaiting spends the tap's user
    // activation and iOS then drops the custom-scheme navigation the escape
    // needs; the write still lands, because firing a custom scheme backgrounds
    // this webview rather than unloading the document, so the clipboard write
    // finishes behind the App Store. Everyone else leaves through a plain https
    // navigation, which does unload the page, and there awaiting is the only
    // thing that guarantees the token reaches the clipboard first.
    if (startEscape()) return;

    await copying;
    redirectToStore();
  };

  return (
    <div
      className={`
        relative flex h-dvh flex-col items-center justify-center gap-6 overflow-hidden px-6
        ${
          !customHtml
            ? darkTheme
              ? "bg-black text-zinc-50"
              : "bg-white text-slate-900"
            : ""
        }
      `}
    >
      {customHtml && (
        <iframe
          srcDoc={customHtml}
          sandbox=""
          className="absolute inset-0 -z-10 h-full w-full border-0"
          title="Custom background"
        />
      )}
      {!customHtml && appIconSrc && (
        <Image
          src={appIconSrc}
          alt="application icon"
          width={96}
          height={96}
          priority
          className="rounded-2xl"
        />
      )}
      {!customHtml && appName && (
        <p className="-mt-2 text-xl font-semibold">{appName}</p>
      )}
      <div
        className={`
          relative z-10 w-full max-w-sm rounded-2xl p-6
          ${
            darkTheme
              ? "border border-zinc-800 bg-zinc-950"
              : `border border-slate-200 bg-white shadow-sm`
          }
        `}
      >
        <h1
          className={`
            text-xl font-semibold
            ${darkTheme ? "text-zinc-50" : "text-slate-900"}
          `}
        >
          {copy.title}
        </h1>
        <p
          className={`
            mt-2 text-sm
            ${darkTheme ? "text-zinc-400" : "text-slate-500"}
          `}
        >
          {copy.description}
        </p>

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={handleCopyAndInstall}
            className={darkTheme ? BTN_PRIMARY_DARK : BTN_PRIMARY_LIGHT}
          >
            {isCopied ? (
              <>
                <Check className="mr-2 inline-block size-4" />
                {copy.copied}
              </>
            ) : (
              copy.copyAndInstall
            )}
          </button>
          <button
            type="button"
            onClick={redirectToStore}
            className={darkTheme ? BTN_SECONDARY_DARK : BTN_SECONDARY_LIGHT}
          >
            {copy.installNow}
          </button>
        </div>

        <div
          className={`
            mt-6 flex items-center justify-center gap-2 text-[15px]
            ${darkTheme ? `text-zinc-400` : `text-slate-500`}
          `}
        >
          <DetourIcon className="size-5" />
          <span>
            {copy.footerPrefix}{" "}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              href={LANDING_PAGE_URL}
            >
              Detour
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}

export default GetAppWithLinkCopy;
